/**
 * Two-factor authentication required for administrators. Dependency-free so
 * node:test can import it (see mfaPolicy.test.js).
 *
 * The rule itself lives on the backend (backend/power_backend/userprofile/
 * mfa_policy.py): an admin who signed in with a password and has no
 * authenticator app, security key or passkey gets `user.mfa.setupRequired` in the session payload, and
 * every API call outside the auth endpoints is refused with a 403 whose body
 * has `code: "mfa_setup_required"`. This module only decides where the SPA
 * sends them.
 */

export const MFA_SETUP_REQUIRED_CODE = 'mfa_setup_required'

/**
 * Where an admin without a second factor chooses one: an authenticator app or
 * a security key or passkey (MfaSetupChoice).
 */
export const MFA_SETUP_PATH = '/account/mfa/setup'

/** The two ways to set one up, offered on MFA_SETUP_PATH and the Security tab. */
export const MFA_TOTP_SETUP_PATH = '/account/mfa/totp/activate'
export const MFA_WEBAUTHN_ADD_PATH = '/account/mfa/webauthn/add'

/** Where everyone manages two-factor authentication. */
export const MFA_SETTINGS_PATH = '/account/security'

/** Shown straight after setup: the recovery codes, then on into the app. */
export const RECOVERY_CODES_AFTER_SETUP_PATH =
    '/account/mfa/recovery-codes?setup=1'

// Pages that must stay reachable while setup is outstanding: the setup pages
// themselves (the choice, the app, a key, recovery codes) and confirming who
// you are before adding one.
const SETUP_ALLOWED_PREFIXES = ['/account/mfa/', '/account/reauthenticate']

export function needsMfaSetup(user) {
    return Boolean(user?.mfa?.setupRequired)
}

export function isMfaSetupRequiredResponse(response) {
    return (
        response?.status === 403 &&
        response?.data?.code === MFA_SETUP_REQUIRED_CODE
    )
}

export function isMfaSetupAllowedPath(pathname) {
    if (typeof pathname !== 'string') return false
    return SETUP_ALLOWED_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

/**
 * Where to send the browser after an API response, or null to stay put.
 * A refusal while already on a setup page must not redirect again, or the
 * page would reload in a loop.
 */
export function mfaSetupRedirect(response, pathname) {
    if (!isMfaSetupRequiredResponse(response)) return null
    if (isMfaSetupAllowedPath(pathname)) return null
    return MFA_SETUP_PATH
}
